import { useClientPortalAPI, useSubmit } from "@certn/api";
import {
  Layout,
  SecondaryTopNav,
  PrimaryButton,
  Container,
  Stack,
  Grid,
  Text,
  showToast,
  Loader,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { Formik } from "formik";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { object, string } from "yup";

import { HeaderCard } from "./HeaderCard";
import { ActionsDropdown } from "../ActionsDropdown";
import { CredentialDetails } from "../components/CredentialDetails";
import { useCredentialVerificationTemplatesInPackages } from "../hooks";
import { CustomInstructionsFormSection } from "../../templates/CustomInstructionsFormSection";
import { CREDENTIAL_DEFAULT_INSTRUCTIONS } from "../../templates/customInstructionsConstants";
import { customInstructionsSchemaField } from "../../templates/customInstructionsValidation";

export function CredentialTemplate() {
  const navigate = useNavigate();
  const { templateId } = useParams();

  const schema = object().shape({
    name: string().required().label(t("Template name")),
    custom_instructions: customInstructionsSchemaField(),
  });

  const templateIdsBelongingToActivePackages =
    useCredentialVerificationTemplatesInPackages();

  const { data, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/credential-verification/${templateId}/`
  );

  const { data: allTemplates } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/credential-verification/`
  );

  const activeTemplates = allTemplates?.results.filter(
    (template) => template.is_active === true
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/credential-verification/${templateId}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Credential verification template has been updated."), {
          type: "success",
        });
        navigate("/settings/credential-verification-templates");
      },
    }
  );

  const handleExitForm = () => {
    navigate("/settings/credential-verification-templates");
  };

  if (!data) {
    return <Loader />;
  }

  return (
    <Formik
      initialValues={{
        name: data.name,
        check_arguments: data.check_arguments,
        custom_instructions:
          (data.check_arguments as { custom_instructions?: string } | null)
            ?.custom_instructions ?? "",
      }}
      validationSchema={schema}
      onSubmit={(values) =>
        submit({
          name: values.name,
          custom_instructions: values.custom_instructions || "",
        })
      }
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <Layout
            layout="narrow"
            header={
              <SecondaryTopNav
                testID="close-button"
                leftIcon="close"
                onleftIconOnPress={handleExitForm}
              >
                <PrimaryButton
                  onPress={() => handleSubmit()}
                  isLoading={isSubmitting}
                >
                  {t("Save changes")}
                </PrimaryButton>
              </SecondaryTopNav>
            }
          >
            <Container>
              <Stack gap="space-3">
                <Text as="h1" style="global/title-medium">
                  {t("Credential verification details")}
                </Text>
              </Stack>

              <Grid form>
                <HeaderCard
                  name={data.name}
                  trailing={
                    <ActionsDropdown
                      templateIdsBelongingToActivePackages={
                        templateIdsBelongingToActivePackages
                      }
                      templateId={data.id}
                      name={data.name}
                      isActive={data.is_active}
                      numActiveTemplates={activeTemplates?.length || 0}
                      mutate={mutate}
                    />
                  }
                />
                <TextInputFormikField
                  name="name"
                  testID="template-name"
                  isRequired
                  label={t("Template name")}
                  description={t(
                    "Used internally to identify the template. The applicant will not see this."
                  )}
                />
                <CredentialDetails
                  checkArguments={data.check_arguments ?? undefined}
                  isReadOnly={true}
                />
                <CustomInstructionsFormSection
                  fieldName="custom_instructions"
                  defaultInstructionsPlaceholder={CREDENTIAL_DEFAULT_INSTRUCTIONS()}
                />
              </Grid>
            </Container>
          </Layout>
        );
      }}
    </Formik>
  );
}
